var _ = require('underscore');

// Fallback memory store for sessions
const memoryStore = {};

const CODE_BOOK_KEY = 'code_book';
const CODE_BAR_KEY = 'code_bar';
const TRANSCRIPT_DRAFT_KEY = 'transcript_draft';
const MEMO_DRAFT_KEY = 'memo_draft';

function safeJSONParse(value) {
  try {
    return JSON.parse(value);
  } catch (e) {
    return null;
  }
}

function safeJSONStringify(value) {
  try {
    return JSON.stringify(value);
  } catch (e) {
    return null;
  }
}

var LocalAPI = {
  _getKey(key, projectID)
  {
    return `${key}_${projectID}`;
  },

  _getCodeBookKey(projectID)
  {
    return this._getKey(CODE_BOOK_KEY, projectID)
  },

  _getCodeBarKey(projectID)
  {
    return this._getKey(CODE_BAR_KEY, projectID)
  },

  _getFromStorage(key) {
    try {
      if (localStorage) {
        const value = safeJSONParse(localStorage.getItem(key));
        if (value !== null) {
          return value;
        }
      }
    } catch (e) {
      // If localStorage is unavailable, fallback to memoryStore
    }
    // Also reached when localStorage has no value: a write that fell back to
    // memoryStore (e.g. setItem threw) must be readable in the same session
    return memoryStore[key] || null;
  },

  _setToStorage(key, value) {
    try {
      if (localStorage) {
        if (value === null || value === undefined) {
          localStorage.removeItem(key);
          delete memoryStore[key];
          return;
        }
        const stringValue = safeJSONStringify(value);
        if (stringValue) {
          localStorage.setItem(key, stringValue);
          // localStorage now holds the value; drop any stale fallback copy
          delete memoryStore[key];
        }
        return;
      }
    } catch (e) {
      // If localStorage is unavailable, fallback to memoryStore
    }
    if (value === null || value === undefined) {
      delete memoryStore[key];
    } else {
      memoryStore[key] = value;
    }
  },

  _getAllKeysWithPrefix(prefix) {
    const keys = new Set();
    try {
      if (localStorage) {
        // Get all keys from localStorage object
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith(prefix)) {
            keys.add(key);
          }
        });
      }
    } catch (e) {
      // localStorage unavailable — the memoryStore keys below still apply
    }
    // Include fallback-stored keys so clearing sweeps memoryStore too
    Object.keys(memoryStore).forEach(key => {
      if (key.startsWith(prefix)) {
        keys.add(key);
      }
    });
    return Array.from(keys);
  },

  // Shared per-key draft store: one namespaced key per id, empty value
  // removes the key
  _saveDraft(prefix, id, value) {
    if (!id) return;
    this._setToStorage(this._getKey(prefix, id), value || null);
  },

  _getDraft(prefix, id, defaultValue) {
    if (!id) return defaultValue;
    return this._getFromStorage(this._getKey(prefix, id)) || defaultValue;
  },

  _clearDraft(prefix, id) {
    if (!id) return;
    this._setToStorage(this._getKey(prefix, id), null);
  },

  _clearAllWithPrefix(prefix) {
    this._getAllKeysWithPrefix(prefix).forEach(key => {
      this._setToStorage(key, null);
    });
  },

  // Everything a signed-out user shouldn't leave behind on a shared machine
  clearAllDrafts() {
    this._clearAllWithPrefix(TRANSCRIPT_DRAFT_KEY);
    this._clearAllWithPrefix(MEMO_DRAFT_KEY);
  },

  clearAllTranscriptDrafts() {
    this._clearAllWithPrefix(TRANSCRIPT_DRAFT_KEY);
  },

  getCodeObject(key)
  {
    return this._getFromStorage(key) || {};
  },

  getCodeBook(projectID)
  {
    return this.getCodeObject(this._getCodeBookKey(projectID), projectID);
  },

  getCodeBar(projectID)
  {
    return this.getCodeObject(this._getCodeBarKey(projectID), projectID);
  },

  saveTranscriptDraft(projectID, name, description) {
    this._saveDraft(TRANSCRIPT_DRAFT_KEY, projectID, { name, description });
  },

  getTranscriptDraft(projectID) {
    return this._getDraft(TRANSCRIPT_DRAFT_KEY, projectID, { name: '', description: '' });
  },

  clearTranscriptDraft(projectID) {
    this._clearDraft(TRANSCRIPT_DRAFT_KEY, projectID);
  },

  saveMemoDraft(excerptID, text) {
    this._saveDraft(MEMO_DRAFT_KEY, excerptID, text);
  },

  getMemoDraft(excerptID) {
    return this._getDraft(MEMO_DRAFT_KEY, excerptID, '');
  },

  clearMemoDraft(excerptID) {
    this._clearDraft(MEMO_DRAFT_KEY, excerptID);
  },

  clearAllMemoDrafts() {
    this._clearAllWithPrefix(MEMO_DRAFT_KEY);
  },

  _setCodeObject(key, originalMapper, codeID, collapse)
  {
    const value = {
      ...originalMapper,
      [codeID]: collapse,
    };
    this._setToStorage(key, value);
  },

  _setCodeBook(projectID, codeID, collapse)
  {
    var key = this._getCodeBookKey(projectID);
    const originalMapper = this.getCodeBook(projectID);
    this._setCodeObject(key, originalMapper, codeID, collapse);
  },

  collapseCode(projectID, codeID)
  {
    this._setCodeBook(projectID, codeID, true);
  },

  expandCode(projectID, codeID)
  {
    this._setCodeBook(projectID, codeID, false);
  },

  _setCodeBar(projectID, codeID, collapse)
  {
    var key = this._getCodeBarKey(projectID);
    const originalMapper = this.getCodeBar(projectID);
    this._setCodeObject(key, originalMapper, codeID, collapse);
  },

  collapseCodeBar(projectID, codeID)
  {
    this._setCodeBar(projectID, codeID, true);
  },

  expandCodeBar(projectID, codeID)
  {
    this._setCodeBar(projectID, codeID, false);
  },

  setTranscriptLocation(transcriptID, loc, page) {
    if (!!transcriptID && !!loc && !!page) {
      const value = { loc, page };
      this._setToStorage(transcriptID, value);
    }
  },

  getTranscriptLocation(transcriptID) {
    if (!!transcriptID) {
      return this._getFromStorage(transcriptID) || {};
    }
    return {};
  },
};

export default LocalAPI;
